import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import { generateLine, isHovering2 } from "./StraightLine";

import {
  isDefined,
  isNotDefined,
  noop,
  hexToRGBA,
  getStrokeDasharray,
} from "../../utils";

class PitchforkLines extends Component {
  constructor(props) {
    super(props);

    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  isHover(moreProps) {
    const { tolerance, onHover } = this.props;
    const { mouseXY } = moreProps;
    const [mouseX, mouseY] = mouseXY;

    let hovering = false;
    if (isDefined(onHover)) {
      const lines = helper(this.props, moreProps);

      for (let i = 0; i < lines.length; i++) {
        const line1 = lines[i];

        // For rays, we need to check if mouse is near the visible portion
        // Extend bounds check to accommodate rays extending to edge
        const left = Math.min(line1.x1, line1.x2) - tolerance;
        const right = Math.max(line1.x1, line1.x2) + tolerance;
        const top = Math.min(line1.y1, line1.y2) - tolerance;
        const bottom = Math.max(line1.y1, line1.y2) + tolerance;

        const isWithinLineBounds =
          mouseX >= left &&
          mouseX <= right &&
          mouseY >= top &&
          mouseY <= bottom;

        hovering =
          isWithinLineBounds &&
          isHovering2(
            [line1.x1, line1.y1],
            [line1.x2, line1.y2],
            mouseXY,
            tolerance,
          );

        if (hovering) break;
      }
    }
    return hovering;
  }

  drawOnCanvas(ctx, moreProps) {
    const { stroke, strokeWidth, strokeOpacity, strokeDasharray } = this.props;

    const lines = helper(this.props, moreProps);

    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = hexToRGBA(stroke, strokeOpacity);
    ctx.setLineDash(getStrokeDasharray(strokeDasharray).split(","));

    lines.forEach((line, idx) => {
      const { x1, y1, x2, y2 } = line;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });
  }

  renderSVG(moreProps) {
    const { stroke, strokeWidth, strokeOpacity, strokeDasharray } = this.props;

    const lines = helper(this.props, moreProps);

    return (
      <g>
        {lines.map((each, idx) => {
          const { x1, y1, x2, y2, type } = each;
          return (
            <line
              key={idx}
              strokeWidth={strokeWidth}
              stroke={stroke}
              strokeOpacity={strokeOpacity}
              strokeDasharray={getStrokeDasharray(strokeDasharray)}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
            />
          );
        })}
      </g>
    );
  }

  render() {
    const { selected, interactiveCursorClass } = this.props;
    const { onDragStart, onDrag, onDragComplete, onHover, onUnHover } =
      this.props;

    return (
      <GenericChartComponent
        isHover={this.isHover}
        svgDraw={this.renderSVG}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        selected={selected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={["mousemove", "mouseleave", "pan", "drag", "xAxisZoom"]}
      />
    );
  }
}

/**
 * Helper function to calculate Andrews' Pitchfork ray coordinates
 *
 * Andrews' Pitchfork consists of:
 * 1. Segment line: connects p1 to p2 (the two "prongs")
 * 2. Median ray: from p0 through the midpoint of p1-p2, extended infinitely
 * 3. Upper parallel ray: from p1, parallel to median, extended infinitely
 * 4. Lower parallel ray: from p2, parallel to median, extended infinitely
 *
 * Algorithm from ChartIQ implementation (lines 11360-11367)
 */
function helper(props, moreProps) {
  const { x0, y0, x1, y1, x2, y2 } = props;

  const {
    xScale,
    chartConfig: { yScale },
  } = moreProps;

  if (isNotDefined(x0) || isNotDefined(x1) || isNotDefined(x2)) {
    return [];
  }

  // Convert data coordinates to pixel coordinates
  const px0 = xScale(x0);
  const py0 = yScale(y0);
  const px1 = xScale(x1);
  const py1 = yScale(y1);
  const px2 = xScale(x2);
  const py2 = yScale(y2);

  const lines = [];

  // 1. Segment line: p1 to p2 (not extended, just a line segment)
  const segment = generateLine({
    type: "LINE", // LINE type = segment, not extended
    start: [x1, y1],
    end: [x2, y2],
    xScale,
    yScale,
  });
  lines.push({
    x1: xScale(segment.x1),
    y1: yScale(segment.y1),
    x2: xScale(segment.x2),
    y2: yScale(segment.y2),
    type: "segment",
  });

  // 2. Median ray: from p0 through midpoint of p1-p2, extended infinitely
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const medianRay = generateLine({
    type: "RAY", // RAY type = extends infinitely to the right
    start: [x0, y0],
    end: [midX, midY],
    xScale,
    yScale,
  });
  lines.push({
    x1: xScale(medianRay.x1),
    y1: yScale(medianRay.y1),
    x2: xScale(medianRay.x2),
    y2: yScale(medianRay.y2),
    type: "median",
  });

  // 3 & 4. Calculate parallel rays using ChartIQ algorithm
  // The parallel rays are offset from p1 and p2 perpendicular to the median direction

  // Calculate perpendicular offset (from ChartIQ lines 11360-11363)
  const z = 50; // projection distance constant
  let yp = 2 * py0 - py1 - py2; // vertical component of offset
  const denom = 2 * px0 - px1 - px2; // horizontal component of offset

  if (denom !== 0) {
    // Adjust sign based on direction
    const zAdjusted = denom < 0 ? -z : z;
    yp *= zAdjusted / denom;

    // Don't render parallel rays if p1 and p2 are at the same position
    if (!(px1 === px2 && py1 === py2)) {
      // 3. Upper parallel ray: from p1, parallel to median
      // Create a second point offset from p1 using the perpendicular vector
      const upperEnd = [
        xScale.invert(px1 - zAdjusted),
        yScale.invert(py1 - yp),
      ];
      const upperRay = generateLine({
        type: "RAY",
        start: [x1, y1],
        end: upperEnd,
        xScale,
        yScale,
      });
      lines.push({
        x1: xScale(upperRay.x1),
        y1: yScale(upperRay.y1),
        x2: xScale(upperRay.x2),
        y2: yScale(upperRay.y2),
        type: "upper",
      });

      // 4. Lower parallel ray: from p2, parallel to median
      const lowerEnd = [
        xScale.invert(px2 - zAdjusted),
        yScale.invert(py2 - yp),
      ];
      const lowerRay = generateLine({
        type: "RAY",
        start: [x2, y2],
        end: lowerEnd,
        xScale,
        yScale,
      });
      lines.push({
        x1: xScale(lowerRay.x1),
        y1: yScale(lowerRay.y1),
        x2: xScale(lowerRay.x2),
        y2: yScale(lowerRay.y2),
        type: "lower",
      });
    }
  }

  return lines;
}

PitchforkLines.propTypes = {
  x0: PropTypes.number.isRequired,
  y0: PropTypes.number.isRequired,
  x1: PropTypes.number.isRequired,
  y1: PropTypes.number.isRequired,
  x2: PropTypes.number.isRequired,
  y2: PropTypes.number.isRequired,

  interactiveCursorClass: PropTypes.string,
  stroke: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number.isRequired,
  strokeDasharray: PropTypes.string,
  strokeOpacity: PropTypes.number.isRequired,

  onDragStart: PropTypes.func.isRequired,
  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,

  tolerance: PropTypes.number.isRequired,
  selected: PropTypes.bool.isRequired,
};

PitchforkLines.defaultProps = {
  onDragStart: noop,
  onDrag: noop,
  onDragComplete: noop,

  strokeWidth: 2,
  tolerance: 7,
  selected: false,
  strokeOpacity: 1,
};

export default PitchforkLines;
